import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, addMonths, monthOf, todayIn } from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';

type View = 'day' | 'week' | 'month';

interface TimeNavProps {
  view: View;
  /** Current anchor: a date for day/week, `YYYY-MM` for month. */
  anchor: string;
  title: string;
  /** Secondary line under the title (e.g. the week being shown). */
  subtitle?: string;
  /** Total shown on the right side of the header. */
  total?: ReactNode;
  /** Takes over navigation instead of routing to /time/* (used by the admin timesheet). */
  onNavigate?: (anchor: string) => void;
  /** Hides the Day/Week/Month switcher where only one view exists. */
  hideViewSwitcher?: boolean;
  /** Use h2 when the page already has its own h1 (the admin timesheet does). */
  headingLevel?: 1 | 2;
  children?: ReactNode;
}

/** View switcher + previous / next / today controls shared by the three time views (FR2). */
export function TimeNav({
  view,
  anchor,
  title,
  subtitle,
  total,
  onNavigate,
  hideViewSwitcher,
  headingLevel = 1,
  children,
}: TimeNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const today = todayIn(me?.timeZone ?? 'UTC');
  const date = view === 'month' ? `${anchor}-01` : anchor;
  const Heading = headingLevel === 1 ? 'h1' : 'h2';

  const go = (next: string) => {
    if (onNavigate) return onNavigate(next);
    if (view === 'day') void navigate({ to: '/time/day/$date', params: { date: next } });
    else if (view === 'week') void navigate({ to: '/time/week/$date', params: { date: next } });
    else void navigate({ to: '/time/month/$month', params: { month: next } });
  };
  const step = (delta: number) => {
    if (view === 'day') go(addDays(anchor, delta));
    else if (view === 'week') go(addDays(anchor, 7 * delta));
    else go(addMonths(anchor, delta));
  };

  const tabs: { view: View; label: string; to: string; params: Record<string, string> }[] = [
    { view: 'day', label: t('nav.day'), to: '/time/day/$date', params: { date } },
    { view: 'week', label: t('nav.week'), to: '/time/week/$date', params: { date } },
    {
      view: 'month',
      label: t('nav.month'),
      to: '/time/month/$month',
      params: { month: monthOf(date) },
    },
  ];

  return (
    <header className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      {/* Title, total and subtitle stay siblings so the heading's parent carries the total. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
        <Heading className="text-lg font-semibold">{title}</Heading>
        {total !== undefined ? (
          <span className="rounded-md bg-accent px-2 py-1 text-sm font-medium tabular-nums">
            {total}
          </span>
        ) : null}
        {subtitle ? <p className="basis-full text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <nav
          aria-label={t('time.title')}
          className={cn('inline-flex rounded-lg bg-muted p-1', hideViewSwitcher && 'hidden')}
        >
          {tabs.map((tab) => (
            <Link
              key={tab.view}
              to={tab.to}
              params={tab.params}
              aria-current={tab.view === view ? 'page' : undefined}
              className={cn(
                'rounded-md px-3 py-1 text-sm font-medium',
                tab.view === view
                  ? 'bg-background shadow'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
        <div className="inline-flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => step(-1)}
            aria-label={t('app.previous')}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(view === 'month' ? monthOf(today) : today)}
          >
            {t('app.today')}
          </Button>
          <Button variant="outline" size="icon" onClick={() => step(1)} aria-label={t('app.next')}>
            <ChevronRight />
          </Button>
        </div>
        {view !== 'month' ? (
          <Input
            type="date"
            value={anchor}
            onChange={(e) => e.target.value && go(e.target.value)}
            aria-label={t('time.date')}
            className="w-40"
          />
        ) : (
          <Input
            type="month"
            value={anchor}
            onChange={(e) => e.target.value && go(e.target.value)}
            aria-label={t('time.date')}
            className="w-40"
          />
        )}
        {children}
      </div>
    </header>
  );
}
